import React, { createContext, useCallback, useMemo, useState, ReactNode } from 'react';
import { isPrerendering } from '@/lib/env';
import {
  clearOptionalStorage,
  clearStoredConsent,
  readStoredConsent,
  writeStoredConsent,
} from '@/lib/consent';

interface CookieConsentContextType {
  /** `true` accepted, `false` rejected, `null` no choice made yet. */
  hasConsented: boolean | null;
  consentToCookies: () => void;
  denyCookies: () => void;
  /** Forget the stored choice and ask again (the footer's "Manage Cookies"). */
  resetConsent: () => void;
  showBanner: boolean;
  setShowBanner: (show: boolean) => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  // Read synchronously so consumers never see a "not loaded yet" state, and the banner
  // is only rendered for visitors who genuinely haven't chosen.
  const [hasConsented, setHasConsented] = useState<boolean | null>(readStoredConsent);
  // (Never part of a prerendered page: the saved HTML must not contain the banner.)
  const [showBanner, setShowBanner] = useState<boolean>(() => !isPrerendering() && readStoredConsent() === null);

  const consentToCookies = useCallback(() => {
    writeStoredConsent(true);
    setHasConsented(true);
    setShowBanner(false);
  }, []);

  const denyCookies = useCallback(() => {
    writeStoredConsent(false);
    clearOptionalStorage();
    setHasConsented(false);
    setShowBanner(false);
  }, []);

  const resetConsent = useCallback(() => {
    clearStoredConsent();
    clearOptionalStorage();
    setHasConsented(null);
    setShowBanner(true);
  }, []);

  const value = useMemo<CookieConsentContextType>(
    () => ({ hasConsented, consentToCookies, denyCookies, resetConsent, showBanner, setShowBanner }),
    [hasConsented, consentToCookies, denyCookies, resetConsent, showBanner]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};

// Export the context for use in the hook
export { CookieConsentContext };
